import { useCallback, useEffect, useState } from "react";
import { MdSettings } from "react-icons/md";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { api } from "../../services/api";

const AJUDA = {
  titulo: "Configurações do Sistema",
  descricao:
    "Ative/desative ou ajuste regras de comportamento do sistema. Cada configuração tem um padrão de fábrica; a fazenda pode sobrescrever esse padrão, e um usuário específico pode ter uma exceção só para ele dentro da fazenda — a exceção do usuário sempre vence, depois o valor da fazenda, e por último o padrão do sistema.",
  secoes: [
    {
      titulo: "Como funciona a hierarquia",
      campos: [
        {
          nome: "Padrão do sistema",
          obrigatorio: false,
          explicacao: "Valor de fábrica, usado quando nada foi configurado. Não precisa fazer nada para usá-lo.",
        },
        {
          nome: "Valor da fazenda",
          obrigatorio: false,
          explicacao: "Ao alternar o toggle, você define o valor para toda a fazenda, sobrescrevendo o padrão do sistema.",
        },
        {
          nome: "Exceção por usuário",
          obrigatorio: false,
          explicacao: "Clique em 'Exceções por usuário' para dar a um colaborador específico um valor diferente do definido para a fazenda.",
        },
      ],
    },
  ],
  dicas: [
    "'Restaurar padrão' remove o valor definido para a fazenda e volta a usar o padrão de fábrica.",
    "Configurações novas aparecem aqui automaticamente conforme forem adicionadas a outros módulos.",
  ],
};

const ORIGEM_LABEL = {
  sistema: { texto: "Padrão do sistema", cor: "bg-gray-100 text-gray-700" },
  fazenda: { texto: "Definido pela fazenda", cor: "bg-blue-100 text-blue-800" },
};

const NOME_MODULO = {
  almoxarifado: "Almoxarifado",
  financeiro: "Financeiro",
  agricultura: "Agricultura",
};

export default function ConfiguracoesSistema() {
  const [configuracoes, setConfiguracoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [salvandoChave, setSalvandoChave] = useState(null);

  const [chaveExpandida, setChaveExpandida] = useState(null);
  const [excecoes, setExcecoes] = useState([]);
  const [carregandoExcecoes, setCarregandoExcecoes] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [novaExcecao, setNovaExcecao] = useState({ usuario_id: "", valor: true });

  const carregarConfiguracoes = useCallback(async () => {
    try {
      setCarregando(true);
      const response = await api.get("/configuracoes/sistema/");
      setConfiguracoes(response.data?.configuracoes || []);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      setErro("Erro ao carregar configurações do sistema.");
    } finally {
      setCarregando(false);
    }
  }, []);

  const carregarColaboradores = useCallback(async () => {
    try {
      const response = await api.get("/configuracoes/perfis-acesso/");
      const dados = response.data?.results || response.data || [];
      setColaboradores(Array.isArray(dados) ? dados.filter((c) => c.ativo) : []);
    } catch (error) {
      console.error("Erro ao carregar colaboradores:", error);
      setColaboradores([]);
    }
  }, []);

  useEffect(() => {
    carregarConfiguracoes();
    carregarColaboradores();
  }, [carregarConfiguracoes, carregarColaboradores]);

  const alternarValor = async (item) => {
    setSalvandoChave(item.chave);
    try {
      await api.post("/configuracoes/sistema/definir/", {
        chave: item.chave,
        valor: !item.valor_efetivo,
      });
      await carregarConfiguracoes();
    } catch (error) {
      console.error("Erro ao salvar configuração:", error);
      alert(error.response?.data?.detail || "Erro ao salvar configuração.");
    } finally {
      setSalvandoChave(null);
    }
  };

  const restaurarPadrao = async (item) => {
    setSalvandoChave(item.chave);
    try {
      await api.post("/configuracoes/sistema/restaurar-padrao/", { chave: item.chave });
      await carregarConfiguracoes();
    } catch (error) {
      console.error("Erro ao restaurar padrão:", error);
      alert(error.response?.data?.detail || "Erro ao restaurar padrão.");
    } finally {
      setSalvandoChave(null);
    }
  };

  const buscarExcecoes = async (chave) => {
    setCarregandoExcecoes(true);
    try {
      const response = await api.get("/configuracoes/sistema/excecoes/", { params: { chave } });
      setExcecoes(response.data || []);
    } catch (error) {
      console.error("Erro ao carregar exceções:", error);
      setExcecoes([]);
    } finally {
      setCarregandoExcecoes(false);
    }
  };

  const alternarExcecoesVisiveis = (chave) => {
    if (chaveExpandida === chave) {
      setChaveExpandida(null);
      return;
    }
    setChaveExpandida(chave);
    setNovaExcecao({ usuario_id: "", valor: true });
    buscarExcecoes(chave);
  };

  const definirExcecao = async (chave) => {
    if (!novaExcecao.usuario_id) {
      alert("Selecione um usuário.");
      return;
    }
    try {
      await api.post("/configuracoes/sistema/definir-excecao/", {
        chave,
        usuario_id: Number(novaExcecao.usuario_id),
        valor: novaExcecao.valor,
      });
      setNovaExcecao({ usuario_id: "", valor: true });
      await buscarExcecoes(chave);
      await carregarConfiguracoes();
    } catch (error) {
      console.error("Erro ao definir exceção:", error);
      alert(error.response?.data?.detail || "Erro ao definir exceção.");
    }
  };

  const removerExcecao = async (chave, usuario_id) => {
    try {
      await api.post("/configuracoes/sistema/remover-excecao/", { chave, usuario_id });
      setExcecoes((prev) => prev.filter((e) => e.usuario_id !== usuario_id));
      carregarConfiguracoes();
    } catch (error) {
      console.error("Erro ao remover exceção:", error);
      alert(error.response?.data?.detail || "Erro ao remover exceção.");
    }
  };

  const configuracoesPorModulo = configuracoes.reduce((acc, item) => {
    (acc[item.modulo] ||= []).push(item);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="p-6 font-medium max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            Configurações do Sistema <MdSettings size={34} />
          </h2>
          <AjudaTela {...AJUDA} />
        </div>

        {erro && <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 border border-red-200">{erro}</div>}

        {carregando ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : (
          Object.entries(configuracoesPorModulo).map(([modulo, itens]) => (
            <div key={modulo} className="bg-white rounded-lg shadow-md border border-gray-200 mb-8 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-800">{NOME_MODULO[modulo] || modulo}</h3>
              </div>

              <div className="divide-y divide-gray-200">
                {itens.map((item) => {
                  const origem = ORIGEM_LABEL[item.origem] || ORIGEM_LABEL.sistema;
                  return (
                    <div key={item.chave} className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800">{item.rotulo}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${origem.cor}`}>{origem.texto}</span>
                            {item.total_excecoes_usuario > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                                {item.total_excecoes_usuario} exceção(ões) por usuário
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{item.descricao}</p>
                        </div>

                        <button
                          type="button"
                          onClick={() => alternarValor(item)}
                          disabled={salvandoChave === item.chave}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                            item.valor_efetivo ? "bg-green-600" : "bg-gray-300"
                          }`}
                          title={item.valor_efetivo ? "Ligado" : "Desligado"}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              item.valor_efetivo ? "translate-x-6" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>

                      <div className="mt-2 flex gap-4 text-sm">
                        {item.origem === "fazenda" && (
                          <button
                            type="button"
                            onClick={() => restaurarPadrao(item)}
                            className="text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Restaurar padrão
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => alternarExcecoesVisiveis(item.chave)}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {chaveExpandida === item.chave ? "Ocultar exceções por usuário" : "Exceções por usuário"}
                        </button>
                      </div>

                      {chaveExpandida === item.chave && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          {carregandoExcecoes ? (
                            <p className="text-sm text-gray-500">Carregando...</p>
                          ) : (
                            <>
                              {excecoes.length === 0 && (
                                <p className="text-sm text-gray-500 mb-3">Nenhuma exceção definida para esta configuração.</p>
                              )}
                              {excecoes.map((e) => (
                                <div key={e.usuario_id} className="flex items-center justify-between py-1 text-sm">
                                  <span>
                                    {e.usuario_nome} — <strong>{e.valor ? "Ligado" : "Desligado"}</strong>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => removerExcecao(item.chave, e.usuario_id)}
                                    className="text-red-600 hover:text-red-800 hover:underline"
                                  >
                                    Remover
                                  </button>
                                </div>
                              ))}

                              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                                <select
                                  value={novaExcecao.usuario_id}
                                  onChange={(ev) => setNovaExcecao((prev) => ({ ...prev, usuario_id: ev.target.value }))}
                                  className="flex-1 border border-green-500 p-2 rounded text-sm"
                                >
                                  <option value="">Selecione um usuário...</option>
                                  {colaboradores
                                    .filter((c) => !excecoes.some((e) => e.usuario_id === c.usuario))
                                    .map((c) => (
                                      <option key={c.usuario} value={c.usuario}>
                                        {c.nome_completo || c.username}
                                      </option>
                                    ))}
                                </select>
                                <select
                                  value={novaExcecao.valor ? "true" : "false"}
                                  onChange={(ev) => setNovaExcecao((prev) => ({ ...prev, valor: ev.target.value === "true" }))}
                                  className="border border-green-500 p-2 rounded text-sm"
                                >
                                  <option value="true">Ligado</option>
                                  <option value="false">Desligado</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => definirExcecao(item.chave)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm"
                                >
                                  Adicionar
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
}
